import type { DealFormData } from "../../types/deal";
import type { ImportRow } from "../../types/import";
import type { ActivityFormData, CommentFormData, ContactForm, Lead, LeadFormData, chatContactForm } from "../../types/lead";
import { apiClient } from "../core/apiClient";

interface getLeadResult {
  leads: Lead[];
  pagination: {
    page: number,
    limit: number,
    total: number,
    pages: number
  }
}

interface getStatusCountResult {
  status_counts: [
    {
      status_id: number,
      status_name: string,
      lead_count: number,
      process: {
        id: number,
        stage: number
      }
    }
  ],
  total_statuses: number
}

interface postLeadResult {
  user: {
    employeeNo: string,
    name: string,
    userType: string,
    doorRight: string
  }
}

interface results<T = any> {
  data: T
}

interface postDealResult {
  user: {
    employeeNo: string,
    name: string,
    userType: string,
    doorRight: string
  }
}


export class LeadService {

  async getLeads(params?: { page?: number }): Promise<getLeadResult> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';

    const endpoint = `/leads${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<getLeadResult>(endpoint, { timeout: 50000 });
    return response.data;
  }

  async getLeadsbyId(id: number): Promise<Lead> {
    const endpoint = `/leads/${id}`;
    const response = await apiClient.get<Lead>(endpoint);
    return response.data;
  }

  async getProcesses(): Promise<results> {
    const response = await apiClient.get<results>(`/leads/processes`);

    return response;
  }

  async getActivities(id: number): Promise<results> {
    const response = await apiClient.get<results>(`/activity-leads/lead/${id}`);
    return response;
  }

  async getUsers(): Promise<results> {
    const response = await apiClient.get<results>(`/users`);
    return response;
  }

  async getComments(id: number): Promise<results> {
    const response = await apiClient.get<results>(`/comments-leads/lead/${id}`);
    return response;
  }

  async getStatusCount(): Promise<getStatusCountResult> {
    const response = await apiClient.get<getStatusCountResult>(`/leads/status/count`);
    return response.data;
  }

  async searchPhoneLead(phone: string, lead_id: number): Promise<results> {
    const response = await apiClient.get(`/leads/search-contact/${phone}/${lead_id}`);
    return response
  }


  async createLead(data: LeadFormData): Promise<{ success: Boolean, error?: string }> {
    try {
      const response = await apiClient.post<postLeadResult>('/leads', data);
      if (response.state) {
        return {
          success: response.state
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async createDeal(data: DealFormData): Promise<{ success: Boolean, error?: string }> {
    try {
      const response = await apiClient.post<postDealResult>('/deals', data);
      if (response.state) {
        return {
          success: response.state
        };
      }
      return {
        success: false
      };
    } catch (error) {
      console.error('Error al crear deal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async createComment(data: CommentFormData): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.post<postLeadResult>('/comments-leads', data);
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async createActivity(data: ActivityFormData): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.post<postLeadResult>('/activity-leads', data);
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async updateStatus(id: number, lead_process_id: number, lead_status_id: number): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const process_id = Number(lead_process_id);
      const status_id = Number(lead_status_id);
      const response = await apiClient.patch<postLeadResult>(`/leads/${id}/status`, { lead_process_id: process_id, lead_status_id: status_id });
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async updateLead(formData: Lead): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.put<postLeadResult>(`/leads/${formData.id}`, formData);
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async createAsesor(lead_id: number, user_id: number): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {

      const response = await apiClient.post<postLeadResult>(`/owner-leads`, { lead_id, user_id });
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }


  async updateAsesor(lead_id: number, user_id: number): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.put<postLeadResult>(`/owner-leads/${lead_id}`, { user_id });
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async createContact(formData: ContactForm): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.post<postLeadResult>(`/contacts`, formData);
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async createLeadsMass(formData: ImportRow[]): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.post<postLeadResult>(`/leads/mass`, formData);
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async bulkAssignLeads(leadIds: number[], userId: number): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.post('/owner-leads/bulk-assign', {
        lead_ids: leadIds,
        user_id: userId
      });
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  };

  async createContactChat(data: chatContactForm): Promise<{ success: Boolean, error?: string, message?: string, contactId?: string }> {
    try {
      const response = await apiClient.post('/leads/create-chat-contact', 
        {data: data}
      );
      
      if (response.data.contactId) {
        return {
          success: response.data.state,
          contactId: response.data.contactId
        };
      }

      return {
          success: response.state,
          message: response.message
        };
    } catch (error) {
      
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  };


}

export const leadService = new LeadService();